import { Pressable, ScrollView, Text, View, Platform } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Query } from '@/utils/react-query/Queries';
import InputSection from '@/components/General/inputs/InputSection';
import { useState, useMemo, useCallback } from 'react';
import SheetSelector from '@/components/General/inputs/SheetSelector';
import SectionTitle from '@/components/General/SectionTitle';
import Unicon from '@/components/General/Unicon';
import { ActivityIndicator } from 'react-native-paper';
import Toast from 'react-native-toast-message';
import { mergeSmallGroups } from '@/data/smallGroups';
import useLoader from '@/utils/hooks/useLoader';
import { showAlert } from '@/utils/showAlert';
import { usePathname } from 'expo-router';
import SolidButton from '@/components/General/SolidButton';
import Callout from '@/components/General/Callout';

const STEP_SELECT = 0;
const STEP_REVIEW = 1;

export default function MergeSmallGroupsScreen() {
    const [currentStep, setCurrentStep] = useState(STEP_SELECT);
    const [selectedSource, setSelectedSource] = useState(null);
    const [selectedTarget, setSelectedTarget] = useState(null);

    const loader = useLoader();
    const pathname = usePathname();
    const queryClient = useQueryClient();

    const { data: sourceGroup, isLoading: loadingSource } = Query.smallGroups.byIdForMerge({
        id: selectedSource?.id,
        options: { enabled: selectedSource != null }
    });
    const { data: targetGroup, isLoading: loadingTarget } = Query.smallGroups.byIdForMerge({
        id: selectedTarget?.id,
        options: { enabled: selectedTarget != null }
    });

    const mergeMutation = useMutation({
        mutationFn: mergeSmallGroups,
        onSuccess: () => {
            Toast.show({ type: 'success', text2: "Small Groups have been merged successfully!" });
            queryClient.invalidateQueries({ queryKey: ['smallGroups'] });
            queryClient.invalidateQueries({ queryKey: ['dashboardData'] });
            resetForm();
            loader.disable();
        },
        onError: () => {
            loader.disable();
        }
    });

    const resetForm = useCallback(() => {
        setSelectedSource(null);
        setSelectedTarget(null);
        setCurrentStep(STEP_SELECT);
    }, []);

    const sourceSessionCount = useMemo(() => {
        return sourceGroup?.Event?.EventInstances?.length || 0;
    }, [sourceGroup]);

    const targetSessionCount = useMemo(() => {
        return targetGroup?.Event?.EventInstances?.length || 0;
    }, [targetGroup]);

    const handleMerge = useCallback(() => {
        showAlert({
            title: "Confirm Merge",
            subtitle: `This will move ${sourceSessionCount} session(s) from "${sourceGroup?.name}" into "${targetGroup?.name}", then delete "${sourceGroup?.name}". This action cannot be undone.`,
            confirmText: "Merge Small Groups",
            onConfirm: () => {
                loader.enable();
                mergeMutation.mutate({
                    sourceId: sourceGroup.id,
                    targetId: targetGroup.id,
                });
            }
        });
    }, [sourceGroup, targetGroup, sourceSessionCount]);

    const canProceed = useMemo(() => {
        switch (currentStep) {
            case STEP_SELECT:
                return selectedSource && selectedTarget && selectedSource.id !== selectedTarget?.id && !loadingSource && !loadingTarget;
            case STEP_REVIEW:
                return true;
            default:
                return false;
        }
    }, [currentStep, selectedSource, selectedTarget, loadingSource, loadingTarget]);

    const isLoading = loadingSource || loadingTarget;

    const renderStepIndicator = () => (
        <View style={styles.stepIndicatorContainer}>
            {['Select', 'Review'].map((label, index) => (
                <Pressable
                    key={label}
                    style={styles.stepItem}
                    onPress={() => index < currentStep && setCurrentStep(index)}
                    disabled={index >= currentStep}
                >
                    <View style={styles.stepCircle(currentStep === index, currentStep > index)}>
                        {currentStep > index ? (
                            <Unicon name="checkmark" size={14} colorVariant="onPrimary" />
                        ) : (
                            <Text style={styles.stepNumber(currentStep === index)}>{index + 1}</Text>
                        )}
                    </View>
                    <Text style={styles.stepLabel(currentStep === index)}>{label}</Text>
                    {index < 1 && <View style={styles.stepConnector(currentStep > index)} />}
                </Pressable>
            ))}
        </View>
    );

    const renderGroupCard = (group, role) => {
        if (!group) return null;
        const isSource = role === 'source';
        const sessionCount = isSource ? sourceSessionCount : targetSessionCount;

        return (
            <View style={styles.groupCard(isSource)}>
                <View style={styles.groupCardHeader}>
                    <View style={styles.groupCardIcon(isSource)}>
                        <Unicon name={isSource ? "trash-outline" : "checkmark-circle-outline"} size={20} colorVariant={isSource ? "error" : "success"} />
                    </View>
                    <View style={styles.groupCardInfo}>
                        <Text style={styles.groupName}>{group.name}</Text>
                        <Text style={styles.groupMeta}>{group.SmallGroupType?.name || 'Small Group'} · {group.status}</Text>
                    </View>
                    <View style={styles.roleBadge(isSource)}>
                        <Text style={styles.roleBadgeText(isSource)}>{isSource ? 'Duplicate' : 'Keep'}</Text>
                    </View>
                </View>

                <View style={styles.groupCardDetails}>
                    {group.Leader && (
                        <View style={styles.detailRow}>
                            <Unicon name="person-outline" size={14} colorVariant="textSecondary" />
                            <Text style={styles.detailText}>Leader: {group.Leader.firstName} {group.Leader.lastName}</Text>
                        </View>
                    )}
                    <View style={styles.detailRow}>
                        <Unicon name="calendar-outline" size={14} colorVariant="textSecondary" />
                        <Text style={styles.detailText}>{sessionCount} session{sessionCount !== 1 ? 's' : ''}</Text>
                    </View>
                    <View style={styles.detailRow}>
                        <Unicon name="people-outline" size={14} colorVariant="textSecondary" />
                        <Text style={styles.detailText}>{group.Students?.length || 0} member{group.Students?.length !== 1 ? 's' : ''}</Text>
                    </View>
                </View>

                <View style={styles.groupCardFooter}>
                    <Unicon name={isSource ? "close-circle" : "checkmark-circle"} size={18} colorVariant={isSource ? "error" : "success"} />
                    <Text style={styles.groupCardFooterText}>
                        {isSource ? "This Small Group will be deleted after merge" : "Sessions will be merged into this Small Group"}
                    </Text>
                </View>
            </View>
        );
    };

    const renderSelectStep = () => (
        <View style={styles.stepContent}>
            <Callout
                type="info"
                title="How Merge Works"
                message="Select the duplicate Small Group and the one you want to keep. All sessions from the duplicate will be moved to the kept Small Group, then the duplicate will be deleted."
            />

            <InputSection
                title="Duplicate (will be deleted)"
                description="Select the Small Group that is a duplicate. Its sessions will be moved."
            >
                <SheetSelector
                    type="smallGroups"
                    title="Duplicate Small Group"
                    fromScreen={pathname}
                    value={selectedSource}
                    excludeIds={selectedTarget ? [selectedTarget.id] : []}
                    onChange={setSelectedSource}
                />
            </InputSection>

            <InputSection
                title="Keep (merge into)"
                description="Select the Small Group you want to keep. Sessions from the duplicate will be added here."
            >
                <SheetSelector
                    type="smallGroups"
                    title="Target Small Group"
                    fromScreen={pathname}
                    value={selectedTarget}
                    excludeIds={selectedSource ? [selectedSource.id] : []}
                    onChange={setSelectedTarget}
                />
            </InputSection>

            {selectedSource?.id === selectedTarget?.id && selectedSource && (
                <Callout type="danger" message="You cannot merge a Small Group with itself. Please select two different Small Groups." />
            )}

            {isLoading && (
                <View style={styles.loadingContainer}>
                    <ActivityIndicator size="large" />
                    <Text style={styles.loadingText}>Loading Small Group details...</Text>
                </View>
            )}

            {sourceGroup && targetGroup && !isLoading && (
                <InputSection title="Preview">
                    {renderGroupCard(sourceGroup, 'source')}
                    <View style={styles.mergeArrow}>
                        <Unicon name="arrow-down" size={24} colorVariant="primary" />
                        <Text style={styles.mergeArrowText}>{sourceSessionCount} session{sourceSessionCount !== 1 ? 's' : ''} will move</Text>
                    </View>
                    {renderGroupCard(targetGroup, 'target')}
                </InputSection>
            )}
        </View>
    );

    const renderReviewStep = () => (
        <View style={styles.stepContent}>
            <Callout
                type="warning"
                title="Review Carefully"
                message="Please review the merge summary below. This action cannot be undone."
            />

            <View style={styles.reviewSection}>
                <SectionTitle title="Small Group to Delete" />
                {renderGroupCard(sourceGroup, 'source')}
            </View>

            <View style={styles.mergeArrow}>
                <View style={styles.mergeArrowLine} />
                <View style={styles.mergeArrowCircle}>
                    <Unicon name="git-merge-outline" size={20} colorVariant="onPrimary" />
                </View>
                <View style={styles.mergeArrowLine} />
            </View>

            <View style={styles.reviewSection}>
                <SectionTitle title="Small Group to Keep" />
                {renderGroupCard(targetGroup, 'target')}
            </View>

            <View style={styles.summaryCard}>
                <Text style={styles.summaryTitle}>Merge Summary</Text>
                <View style={styles.summaryRow}>
                    <Text style={styles.summaryLabel}>Sessions to move</Text>
                    <Text style={styles.summaryValue}>{sourceSessionCount}</Text>
                </View>
                <View style={styles.summaryRow}>
                    <Text style={styles.summaryLabel}>Existing sessions in target</Text>
                    <Text style={styles.summaryValue}>{targetSessionCount}</Text>
                </View>
                <View style={[styles.summaryRow, styles.summaryRowTotal]}>
                    <Text style={styles.summaryLabelBold}>Total sessions after merge</Text>
                    <Text style={styles.summaryValueBold}>{sourceSessionCount + targetSessionCount}</Text>
                </View>
            </View>
        </View>
    );

    const renderStepContent = () => {
        switch (currentStep) {
            case STEP_SELECT:
                return renderSelectStep();
            case STEP_REVIEW:
                return renderReviewStep();
            default:
                return null;
        }
    };

    return (
        <View style={styles.container}>
            {renderStepIndicator()}

            <ScrollView
                style={styles.scrollView}
                contentContainerStyle={styles.scrollContent}
                showsVerticalScrollIndicator={false}
                keyboardShouldPersistTaps="handled"
            >
                {renderStepContent()}
            </ScrollView>

            <View style={styles.navigationBar}>
                <View style={styles.navButtonContainer}>
                    {currentStep > STEP_SELECT && (
                        <SolidButton
                            text="Back"
                            iconName="arrow-back"
                            iconBefore
                            variant="outlined"
                            onPress={() => setCurrentStep(prev => prev - 1)}
                        />
                    )}
                </View>

                <View style={styles.navButtonContainer}>
                    {currentStep < STEP_REVIEW ? (
                        <SolidButton
                            text="Continue"
                            iconName="arrow-forward"
                            disabled={!canProceed}
                            onPress={() => setCurrentStep(prev => prev + 1)}
                        />
                    ) : (
                        <SolidButton
                            text="Merge Small Groups"
                            iconName="git-merge-outline"
                            iconBefore
                            type="success"
                            onPress={handleMerge}
                            processing={mergeMutation.isPending}
                        />
                    )}
                </View>
            </View>
        </View>
    );
}

const styles = StyleSheet.create(({ colors, fonts, shadows }, rt) => ({
    container: {
        flex: 1,
        backgroundColor: 'transparent',
        paddingBottom: Platform.OS === 'ios' && parseInt(Platform.Version, 10) >= 26 && !Platform.isPad && !rt.isLandscape ? 80 : 0,
    },
    scrollView: {
        flex: 1,
    },
    scrollContent: {
        padding: 16,
        paddingBottom: 100,
    },
    stepContent: {
        gap: 16,
    },
    stepIndicatorContainer: {
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        paddingVertical: 20,
        paddingHorizontal: 16,
        backgroundColor: colors.surface,
        borderBottomWidth: 1,
        borderBottomColor: colors.border,
    },
    stepItem: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    stepCircle: (isActive, isCompleted) => ({
        width: 28,
        height: 28,
        borderRadius: 14,
        backgroundColor: isCompleted ? colors.success : isActive ? colors.primary : colors.border,
        justifyContent: 'center',
        alignItems: 'center',
    }),
    stepNumber: (isActive) => ({
        color: isActive ? colors.onPrimary : colors.textSecondary,
        fontSize: 12,
        fontFamily: fonts.bold,
    }),
    stepLabel: (isActive) => ({
        marginLeft: 6,
        fontSize: 12,
        color: isActive ? colors.primary : colors.textSecondary,
        fontFamily: isActive ? fonts.bold : fonts.body,
    }),
    stepConnector: (isCompleted) => ({
        width: rt.screen.width > 500 ? 60 : 30,
        height: 2,
        backgroundColor: isCompleted ? colors.success : colors.border,
        marginHorizontal: 8,
    }),
    groupCard: (isSource) => ({
        backgroundColor: colors.surface,
        borderRadius: 12,
        padding: 16,
        marginVertical: 8,
        borderWidth: 2,
        borderColor: isSource ? colors.error : colors.success,
        borderStyle: isSource ? 'dashed' : 'solid',
        boxShadow: shadows.card,
    }),
    groupCardHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
    },
    groupCardIcon: (isSource) => ({
        width: 40,
        height: 40,
        borderRadius: 20,
        backgroundColor: isSource ? colors.errorContainer : colors.successContainer,
        justifyContent: 'center',
        alignItems: 'center',
    }),
    groupCardInfo: {
        flex: 1,
    },
    groupName: {
        fontSize: 16,
        fontFamily: fonts.bold,
        color: colors.text,
    },
    groupMeta: {
        fontSize: 13,
        color: colors.textSecondary,
        marginTop: 2,
    },
    roleBadge: (isSource) => ({
        paddingHorizontal: 10,
        paddingVertical: 4,
        borderRadius: 12,
        backgroundColor: isSource ? colors.errorContainer : colors.successContainer,
    }),
    roleBadgeText: (isSource) => ({
        fontSize: 11,
        fontFamily: fonts.bold,
        color: isSource ? colors.error : colors.success,
    }),
    groupCardDetails: {
        marginTop: 12,
        paddingTop: 12,
        borderTopWidth: 1,
        borderTopColor: colors.border,
        gap: 6,
    },
    detailRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    detailText: {
        fontSize: 13,
        color: colors.textSecondary,
    },
    groupCardFooter: {
        flexDirection: 'row',
        alignItems: 'center',
        marginTop: 12,
        paddingTop: 12,
        borderTopWidth: 1,
        borderTopColor: colors.border,
        gap: 8,
    },
    groupCardFooterText: {
        fontSize: 12,
        color: colors.textSecondary,
        fontStyle: 'italic',
        flex: 1,
    },
    mergeArrow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        paddingVertical: 4,
    },
    mergeArrowText: {
        fontSize: 13,
        fontFamily: fonts.bold,
        color: colors.primary,
    },
    mergeArrowLine: {
        flex: 1,
        height: 2,
        backgroundColor: colors.border,
    },
    mergeArrowCircle: {
        width: 36,
        height: 36,
        borderRadius: 18,
        backgroundColor: colors.primary,
        justifyContent: 'center',
        alignItems: 'center',
    },
    reviewSection: {
        marginTop: 8,
    },
    summaryCard: {
        backgroundColor: colors.surface,
        borderRadius: 12,
        padding: 16,
        borderWidth: 1,
        borderColor: colors.border,
    },
    summaryTitle: {
        fontSize: 15,
        fontFamily: fonts.bold,
        color: colors.text,
        marginBottom: 12,
    },
    summaryRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingVertical: 8,
    },
    summaryRowTotal: {
        borderTopWidth: 1,
        borderTopColor: colors.border,
        marginTop: 4,
        paddingTop: 12,
    },
    summaryLabel: {
        fontSize: 13,
        color: colors.textSecondary,
    },
    summaryValue: {
        fontSize: 13,
        fontFamily: fonts.bold,
        color: colors.text,
    },
    summaryLabelBold: {
        fontSize: 14,
        fontFamily: fonts.bold,
        color: colors.text,
    },
    summaryValueBold: {
        fontSize: 16,
        fontFamily: fonts.bold,
        color: colors.primary,
    },
    navigationBar: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: 16,
        backgroundColor: colors.surface,
        borderTopWidth: 1,
        borderTopColor: colors.border,
        boxShadow: shadows.card,
    },
    navButtonContainer: {
        minWidth: 100,
    },
    loadingContainer: {
        alignItems: 'center',
        justifyContent: 'center',
        padding: 40,
        gap: 12,
    },
    loadingText: {
        fontSize: 14,
        color: colors.textSecondary,
    },
}));
